import AutomationToolBenefitsDesktop from "@components/AutomationToolBenefitsDesktop";
import AutomationToolBenefitsMobile, { AutomationToolBenefitsProps } from "@components/AutomationToolBenefitsMobile";
import PaymentProcessCard, { PaymentSectionDataProps } from "@components/integration/PaymentProcessCard";
import PaymentStepsAccordion, { PaymentProcess } from "@components/integration/PaymentStepsAccordion";
import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import React from "react";

const herosectionData: HerosectionProps = {
    chipTitle: "WhatsApp automation tool",
    title: "Automate 50% of your customer conversations on WhatsApp",
    description:
        "Improve lead engagement, drive sales, and reduce support tickets for your business with an AI-powered WhatsApp automation tool. Scale your business without increasing your headcount.",
    primaryButtonText: "Start Free Trial",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/wa-automation-tool-hero-image.png",
};

const featureInfo1: FeatureInfo = {
    mainTitle: "Build complex chatbot workflows in a few clicks",
    subtitle: "Create chatbot journeys that convert without a single line of code.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/chatbot-workflow.png",
    featureInfoList: [
        "Execute responsive flows in minutes with our drag-and-drop builder",
        "Deploy use case and industry-specific templates with a single click",
        "Build a complete conversation flow with GenAI prompts and handoff rules.",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Bring your drip marketing campaigns to WhatsApp",
    subtitle: "Automate WhatsApp sequences with 45% more engagement and conversions than emails.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/drip-marketing-campaign.png",
    featureInfoList: [
        "Schedule drip marketing messages spread over several days to nurture prospects",
        "Segment users based on behavior and trigger the most relevant sequences",
        "Set up conditions to assign conversations to bots and humans.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Provide timely alerts, announcements, and reminders",
    subtitle: "Keep your customers informed with automated notifications.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/automated+-notifications.png",
    featureInfoList: [
        "Automate payment requests and reminders with payment gateways, webhooks, and more",
        "Broadcast new offers and announcements when you launch a product or service",
        "Trigger authentication, security, and account related alerts automatically.",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Leverage an automation-first integration ecosystem",
    subtitle: "Our WhatsApp Business automation tool seamlessly communicates with every business-critical tool out there.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/payments-intergration.png",
    featureInfoList: [
        "Update customer records automatically in CRMs such as Zoho, HubSpot, and Odoo",
        "Automate order alerts, cart abandonment nudges, and more from Shopify",
        "Utilize WhatsApp's calendar tools for appointment scheduling and reminders",
    ],
};

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/footprint-real-estate.png",
        chiptitle: "REAL ESTATE",
        title: "Gallabox revolutionizes lead engagement for Footprint Real Estate",
        statsInfoData: [
            { percentage: "150%", label: "Rise in customer conversion" },
            { percentage: "100%", label: "Increase in lead generation" },
            { percentage: "100%", label: "Expansion in customer base" },
        ],
        url: "/case-study/footprint-real-estate",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/thread-bucket-studio/thread-bucket-studio.png",
        chiptitle: "REAL ESTATE",
        title: "Why Thread Bucket Studio chose Gallabox over Wati for seamless team communication",
        statsInfoData: [
            { percentage: "68%", label: "Higher business growth" },
            { percentage: "50%", label: "Rise in employee productivity" },
            { percentage: "30%", label: "Growth in customer base" },
        ],
        url: "/case-study/thread-bucket-studio",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/qatar-insurance-company.png",
        chiptitle: "FINANCE",
        title: "How Gallabox Helped Qatar Insurance Company Generate 2X More Leads on WhatsApp",
        statsInfoData: [
            { percentage: "31%", label: "Increase in team productivity" },
            { percentage: "102%", label: "Growth in lead generation" },
            { percentage: "2X", label: "Improvement in CSAT ratings" },
        ],
        url: "/case-study/qatar-insurance-company",
    },
];

const faqData: FAQSectionProps = {
    title: "FAQs about WhatsApp automation tool",
    faqItems: [
        {
            question: "Can I create automated workflows with the WhatsApp Flows Builder?",
            answer: "Yes, Gallabox’s custom WhatsApp Flows builder helps teams build custom flows for their lead generation, appointment scheduling, feedback surveys, and more. You can also use pre-existing templates and create a custom automated chat flow from scratch.",
        },
        {
            question: "Does the WhatsApp automation tool support message personalization for each customer?",
            answer: "Yes, our WhatsApp automation tool provides variable placeholders for names, previous purchases, or account details. You can also create conditional flows and AI bots that respond based on customer attributes or previous interactions to make it even more personalized.",
        },
        {
            question: "Can I set up automated workflows for customer inquiries and interactions on WhatsApp?",
            answer: "Yes. Our drag-and-drop bot builder makes it simple to create multi-step conversations that vary based on user responses. You can build journeys for lead qualification, appointment booking, product recommendations, feedback collection, and much more without writing any code. ",
        },
        {
            question: "Can I integrate the WhatsApp automation tool with my email marketing or SMS campaigns?",
            answer: "Yes, our WhatsApp automation tool is purpose-built to work alongside your other communication channels. You can integrate your WhatsApp campaigns with email and SMS using marketing automation and CRM tools that help launch campaigns simultaneously. ",
        },
        {
            question: "Is it possible to integrate WhatsApp automation tool with my ecommerce platform for order updates and alerts",
            answer: "Yes. Our WhatsApp automation tool has native integrations with popular ecommerce platforms like Shopify. You can automate order confirmations, shipping updates, delivery notifications, cart abandonment nudges, and after-sale follow-ups on WhatsApp. ",
        },
        {
            question: "Does the WhatsApp automation tool support rich media (images, videos, voice notes) in automated messages?",
            answer: "Yes, our WhatsApp automation tool fully supports rich media messaging. You can include images, videos, PDFs, voice notes, and multiple other file types in your automated messages. This is particularly valuable for sharing product catalogs, demonstration videos, instructional guides, or adding a more personal touch with recorded voice and videos.",
        },
        {
            question: "Does the WhatsApp automation tool support rich media (images, videos, voice notes) in automated messages?",
            answer: "Yes, our WhatsApp automation tool fully supports rich media messaging. You can include images, videos, PDFs, voice notes, and multiple other file types in your automated messages. This is particularly valuable for sharing product catalogs, demonstration videos, instructional guides, or adding a more personal touch with recorded voice and videos.",
        },
    ],
};

const paymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/whatsApp-business-account.png",
        title: "Connect your WhatsApp Business account",
        description: "Sign up for a Gallabox account and connect your WhatsApp Business number. Complete your business profile to start building customer trust.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/bot-templates.png",
        title: "Create your first automation",
        description:
            "Select from our library of proven chatbot and broadcast templates. Customize the existing flows to match your brand voice and business use cases; or build your own bots from scratch with our AI bot builder.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/bot-flow.png",
        title: "Launch and optimize",
        description:
            "Hit “Publish” once you have created the bot’s conversation flow. Test every flow before going live. Track performance metrics and refine the automation from real-time data.",
    },
];

const defaultFAQData: PaymentProcess = {
    title: "How to get started with our WhatsApp automation tool",
    ProcessSteps: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/whatsApp-business-account.png",
            title: "Connect your WhatsApp Business account",
            description: "Sign up for a Gallabox account and connect your WhatsApp Business number. Complete your business profile to start building customer trust.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/bot-templates.png",
            title: "Create your first automation",
            description:
                "Select from our library of proven chatbot and broadcast templates. Customize the existing flows to match your brand voice and business use cases; or build your own bots from scratch with our AI bot builder.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/bot-flow.png",
            title: "Launch and optimize",
            description:
                "Hit “Publish” once you have created the bot’s conversation flow. Test every flow before going live. Track performance metrics and refine the automation from real-time data.",
        },
    ],
};

const automationToolBenefitsData: AutomationToolBenefitsProps = {
    title: "Benefits of using our WhatsApp Business automation tool",
    ProcessSteps: [
        {
            title: "Put your lead qualification on auto-pilot",
            description:
                "Qualify leads 24x7, and save your sales team from doing the monotonous back-and-forth. Instead, automate lead qualification with chatbots and give them warm, high-value prospects who are more likely to convert.",
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/auto-pilot.png",
        },
        {
            title: "Use agentic AI to automate conversion outcomes",
            description:
                "Qualify leads 24x7, and save your sales team from doing the monotonous back-and-forth. Instead, automate lead qualification with chatbots and give them warm, high-value prospects who are more likely to convert.",
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/agentic-ai.png",
        },
        {
            title: "Save hours of manual work while increasing ROI",
            description:
                "Automate replies to common questions from your prospects and customers. Set up conversation flows that guide them through the entire support process. Assign conversations from Click-to-WhatsApp ads, QR codes, and web widgets to the right team or bot. ",
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/automate-replies.png",
        },
    ],
};

const WhatsappAutomation: React.FC = () => {
    return (
        <NewLayout
            description="From automated replies to smart workflows, our WhatsApp automation software helps businesses drive faster sales cycles and support."
            title="Auto-pilot Conversations with WhatsApp Automation Tool"
            canonical="https://gallabox.com/whatsapp-automation"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/whatsapp-automation-tool-og.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-automation-image",
                heroOrOgImageDescription:
                    "Improve lead engagement, drive sales, and reduce support tickets for your business with an AI-powered WhatsApp automation tool. Scale your business without increasing your headcount.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/whatsapp-automation-tool/wa-automation-tool-hero-image.png",
                faq: faqData.faqItems,
                softwareFeatureName: "whatsapp-business-api",
            }}
        >
            <Herosection herosection={herosectionData} />
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos />
            </section>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <FeatureInfoCard {...featureInfo4} removeDivider />

            <AutomationToolBenefitsDesktop automationToolBenefits={automationToolBenefitsData} />

            <AutomationToolBenefitsMobile automationToolBenefits={automationToolBenefitsData} />

            <PaymentProcessCard title="How to get started with our WhatsApp automation tool" paymentSectionData={paymentSectionData} backgroundColor="white" />

            <PaymentStepsAccordion paymentStepsData={defaultFAQData} />
            <div className="mt-20">
                <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            </div>
            <FAQSection faqData={faqData} />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Put your revenue goals on auto-pilot with our WhatsApp automation tool" primaryButtonText="Get free trial" />
        </NewLayout>
    );
};

export default WhatsappAutomation;
